import React, { useState, useMemo, useCallback } from "react";
import { ChevronLeft, ChevronRight, Clock, Dna, AlertCircle, Sun, Moon, Sunrise, Sparkles, Bell, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format, addDays, subDays } from "date-fns";
import { LoadingIndicator } from "@/components/booking/LoadingIndicator";
import { cn } from "@/lib/utils";
import { getTimezoneInfo, convertSlotToTimezone } from "@/lib/timezone/timezone-service";
import { getDateKeyInTimezone } from "@/lib/timezone/date-utils";
import { getAllCalendarConnectionsForUser } from "@/lib/calendar/connection";
import { categorizeTimeSlots, getTimePeriodInfo } from "@/lib/utils/time-categorization";
import { useFeatureFlag, useFeatureFamily } from "@/hooks/useFeatureFlag";

interface TimeSlotStepProps {
  selectedTimezone: string;
  availableSlots: string[];
  onTimeSelected: (time: string) => void;
  selectedDate: Date;
  onPrevDay: () => void;
  onNextDay: () => void;
  troubleshootMode?: boolean;
  loading?: boolean;
  shouldShowSkeleton?: boolean;
  organizerTimezone: string;
  showAllSlots?: boolean;
  enhancedAvailableSlots?: Record<string, { preferred: string[]; available: string[] }>;
  meetingLinkId: string;
  className?: string;
  organizerUserId: string;
  energyPreference?: string;
}

export const TimeSlotStep: React.FC<TimeSlotStepProps> = ({
  selectedTimezone,
  availableSlots,
  onTimeSelected,
  selectedDate,
  onPrevDay,
  onNextDay,
  troubleshootMode = false,
  loading = false,
  shouldShowSkeleton = false,
  organizerTimezone,
  showAllSlots = false,
  enhancedAvailableSlots,
  meetingLinkId,
  className,
  organizerUserId,
  energyPreference
}) => {
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>(() => {
    // Get from localStorage or default to 12h
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('chronocal-time-format');
      return (stored as '12h' | '24h') || '12h';
    }
    return '12h';
  });

  // Feature flags for notifications
  const notificationsEnabled = useFeatureFlag("notifications");
  const notificationFeatures = useFeatureFamily("notifications", [
    "notifications-bell",
    "notifications-popup",
    "notifications-fx"
  ]);

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
    onTimeSelected(time);
  };

  const toggleTimeFormat = () => {
    const newFormat = timeFormat === '12h' ? '24h' : '12h';
    setTimeFormat(newFormat);
    localStorage.setItem('chronocal-time-format', newFormat);
  };

  // Function to format time based on preference
  const formatTimeSlot = (timeString: string) => {
    if (timeFormat === '24h') {
      // Convert 12h format to 24h if needed
      if (timeString.includes('AM') || timeString.includes('PM')) {
        const [time, period] = timeString.split(' ');
        const [hours, minutes] = time.split(':');
        let hour24 = parseInt(hours);
        
        if (period === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
          hour24 = 0;
        }
        
        return `${hour24.toString().padStart(2, '0')}:${minutes}`;
      }
      return timeString; // Already in 24h format
    } else {
      // Convert to 12h format if needed
      if (!timeString.includes('AM') && !timeString.includes('PM')) {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const period = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${hour12}:${minutes} ${period}`;
      }
      return timeString; // Already in 12h format
    }
  };

  // Enhanced slots processing for the selected date
  const enhancedSlotsForDate = useMemo(() => {
    if (!enhancedAvailableSlots || !selectedDate) return null;
    
    const dateKey = getDateKeyInTimezone(selectedDate, organizerTimezone);
    const slots = enhancedAvailableSlots[dateKey] || null;
    
    // Add debugging
    if (troubleshootMode) {
      console.log('[TimeSlotStep] Enhanced slots debug:', {
        dateKey,
        enhancedAvailableSlots,
        slotsForDate: slots,
        showAllSlots
      });
    }
    
    return slots;
  }, [enhancedAvailableSlots, selectedDate, troubleshootMode, showAllSlots]);

  // Convert slots to the selected timezone
  const convertSlots = useCallback((slots: string[]) => {
    return slots.map(slot => {
      try {
        const converted = convertSlotToTimezone(
          {
            startTime: slot,
            endTime: slot,
            sourceTimezone: organizerTimezone
          },
          selectedTimezone,
          selectedDate // Pass the selected date for accurate timezone conversion
        );
        return {
          originalTime: slot,
          convertedTime: converted.startTime,
          displayTime: formatTimeSlot(converted.displayStartTime)
        };
      } catch (error) {
        console.error('Error converting slot time:', error);
        const [hours, minutes] = slot.split(':');
        const referenceDate = selectedDate || new Date();
        const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate(), parseInt(hours), parseInt(minutes), 0, 0);
        const fallbackTime = format(date, 'h:mm a');
        return {
          originalTime: slot,
          convertedTime: slot,
          displayTime: formatTimeSlot(fallbackTime)
        };
      }
    });
  }, [organizerTimezone, selectedTimezone, selectedDate, timeFormat, formatTimeSlot]);

  // Process slots based on whether we're using enhanced mode
  const { preferredSlots, regularSlots, allSlots, hasCalendarError } = useMemo(() => {
    if (troubleshootMode) {
      console.log('[TimeSlotStep] Processing slots:', {
        hasEnhancedSlots: !!enhancedSlotsForDate,
        showAllSlots,
        availableSlots: availableSlots.length,
        enhancedSlotsForDate
      });
    }

    // Check for calendar errors in the available slots
    const calendarError = enhancedSlotsForDate?.available?.some(slot => 
      slot === "Error" || (typeof slot === 'string' && slot.includes('Calendar connection expired'))
    ) || availableSlots.some(slot => 
      slot === "Error" || (typeof slot === 'string' && slot.includes('Calendar connection expired'))
    );

    if (enhancedSlotsForDate && showAllSlots) {
      // Enhanced mode with separate preferred and available slots
      const preferred = convertSlots(enhancedSlotsForDate.preferred);
      const available = convertSlots(enhancedSlotsForDate.available);
      // Combine all slots for UI logic that checks allSlots.length
      const combinedSlots = [...preferred, ...available];
      
      if (troubleshootMode) {
        console.log('[TimeSlotStep] Enhanced mode - showAllSlots:', {
          preferredCount: preferred.length,
          availableCount: available.length,
          totalSlots: combinedSlots.length,
          preferredSlots: enhancedSlotsForDate.preferred,
          availableSlots: enhancedSlotsForDate.available
        });
      }
      
      return {
        preferredSlots: preferred,
        regularSlots: available,
        allSlots: combinedSlots,
        hasCalendarError: calendarError
      };
    } else if (enhancedSlotsForDate && !showAllSlots) {
      // Enhanced mode but only showing preferred slots
      const preferred = convertSlots(enhancedSlotsForDate.preferred);
      
      if (troubleshootMode) {
        console.log('[TimeSlotStep] Enhanced mode - preferred only:', {
          preferredCount: preferred.length,
          preferredSlots: enhancedSlotsForDate.preferred
        });
      }
      
      return {
        preferredSlots: preferred,
        regularSlots: [],
        allSlots: preferred, // Use preferred slots for UI logic that checks allSlots.length
        hasCalendarError: calendarError
      };
    } else {
      // Regular mode - all slots are treated the same
      const all = convertSlots(availableSlots);
      
      if (troubleshootMode) {
        console.log('[TimeSlotStep] Regular mode:', {
          allSlotsCount: all.length,
          availableSlots
        });
      }
      
      return {
        preferredSlots: [],
        regularSlots: [],
        allSlots: all,
        hasCalendarError: calendarError
      };
    }
  }, [enhancedSlotsForDate, showAllSlots, availableSlots, convertSlots, troubleshootMode]);

  // Check if the selected time is an optimal slot
  const isOptimalSlot = useMemo(() => {
    if (!selectedTime) return false;
    return preferredSlots.some(slot => slot.convertedTime === selectedTime);
  }, [selectedTime, preferredSlots]);

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (date: Date) => {
    const tomorrow = addDays(new Date(), 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const getDateLabel = (date: Date) => {
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEE, MMM d");
  };

  const timezoneInfo = getTimezoneInfo(selectedTimezone);

  const totalSlots = preferredSlots.length + regularSlots.length + allSlots.length;
  const shouldShowContent = !shouldShowSkeleton;
  const shouldShowEmptyState = shouldShowContent && !loading && totalSlots === 0 && !hasCalendarError;
  const shouldShowLoadingMessage = shouldShowContent && loading;
  const shouldShowSlots = shouldShowContent && !loading && totalSlots > 0 && !hasCalendarError;
  const shouldShowCalendarError = shouldShowContent && !loading && hasCalendarError;

  const renderSlotGrid = (slots: typeof preferredSlots, label?: string, icon?: React.ReactNode) => {
    if (slots.length === 0) return null;

    return (
      <div className="space-y-3">
        {label && (
          <div className="flex items-center gap-2">
            {icon}
            <h4 className="font-medium text-gray-900">{label}</h4>
            <span className="text-sm text-gray-500">
              {slots.length} slot{slots.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {slots.map((slot, index) => (
            <Button
              key={`${slot.convertedTime}-${index}`}
              variant={selectedTime === slot.convertedTime ? "default" : "outline"}
                                className={cn(
                    "h-10 text-sm font-semibold w-full border-zinc-300 rounded-lg transition-all duration-300",
                    selectedTime === slot.convertedTime 
                      ? isOptimalSlot && preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white border-0 shadow-lg shadow-purple-500/25 transform scale-105"
                        : "bg-primary text-primary-foreground border-primary" 
                      : preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "hover:bg-gradient-to-r hover:from-purple-100 hover:via-pink-100 hover:to-orange-100 hover:border-purple-300 hover:text-purple-700"
                        : "hover:border-primary hover:text-primary"
                  )}
              data-testid={`slot-${slot.displayTime.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
              onClick={() => handleTimeClick(slot.convertedTime)}
            >
              {slot.displayTime}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const renderCategorizedSlots = (slots: typeof preferredSlots, label?: string, icon?: React.ReactNode) => {
    if (slots.length === 0) return null;

    const categorized = categorizeTimeSlots(slots);

    return (
      <div className="space-y-6">
        {label && (
          <div className="flex items-center gap-2">
            {icon}
            <h4 className="font-medium text-gray-900">{label}</h4>
            {/* <span className="text-sm text-gray-500">
              {slots.length} slot{slots.length !== 1 ? 's' : ''}
            </span> */}
          </div>
        )}
        
        {/* Morning Slots */}
        {categorized.morning.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Sunrise className="h-4 w-4 text-orange-500" />
              <h5 className="font-medium text-gray-800">Morning</h5>
              <span className="text-sm text-gray-500">
                {categorized.morning.length} slot{categorized.morning.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
              {categorized.morning.map((slot, index) => (
                <Button
                  key={`morning-${slot.convertedTime}-${index}`}
                  variant={selectedTime === slot.convertedTime ? "default" : "outline"}
                  className={cn(
                    "h-10 text-sm font-semibold w-full border-zinc-300 rounded-lg transition-all duration-300",
                    selectedTime === slot.convertedTime 
                      ? isOptimalSlot && preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white border-0 shadow-lg shadow-purple-500/25 transform scale-105"
                        : "bg-primary text-primary-foreground border-primary" 
                      : preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "hover:bg-gradient-to-r hover:from-purple-100 hover:via-pink-100 hover:to-orange-100 hover:border-purple-300 hover:text-purple-700"
                        : "hover:border-primary hover:text-primary"
                  )}
                  data-testid={`slot-${slot.displayTime.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
                  onClick={() => handleTimeClick(slot.convertedTime)}
                >
                  {slot.displayTime}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Afternoon Slots */}
        {categorized.afternoon.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Sun className="h-4 w-4 text-yellow-500" />
              <h5 className="font-medium text-gray-800">Afternoon</h5>
              <span className="text-sm text-gray-500">
                {categorized.afternoon.length} slot{categorized.afternoon.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
              {categorized.afternoon.map((slot, index) => (
                <Button
                  key={`afternoon-${slot.convertedTime}-${index}`}
                  variant={selectedTime === slot.convertedTime ? "default" : "outline"}
                  className={cn(
                    "h-10 text-sm font-semibold w-full border-zinc-300 rounded-lg transition-all duration-300",
                    selectedTime === slot.convertedTime 
                      ? isOptimalSlot && preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white border-0 shadow-lg shadow-purple-500/25 transform scale-105"
                        : "bg-primary text-primary-foreground border-primary" 
                      : preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "hover:bg-gradient-to-r hover:from-purple-100 hover:via-pink-100 hover:to-orange-100 hover:border-purple-300 hover:text-purple-700"
                        : "hover:border-primary hover:text-primary"
                  )}
                  data-testid={`slot-${slot.displayTime.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
                  onClick={() => handleTimeClick(slot.convertedTime)}
                >
                  {slot.displayTime}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Evening Slots */}
        {categorized.evening.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Moon className="h-4 w-4 text-blue-500" />
              <h5 className="font-medium text-gray-800">Evening</h5>
              <span className="text-sm text-gray-500">
                {categorized.evening.length} slot{categorized.evening.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
              {categorized.evening.map((slot, index) => (
                <Button
                  key={`evening-${slot.convertedTime}-${index}`}
                  variant={selectedTime === slot.convertedTime ? "default" : "outline"}
                  className={cn(
                    "h-10 text-sm font-semibold w-full border-zinc-300 rounded-lg transition-all duration-300",
                    selectedTime === slot.convertedTime 
                      ? isOptimalSlot && preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white border-0 shadow-lg shadow-purple-500/25 transform scale-105"
                        : "bg-primary text-primary-foreground border-primary" 
                      : preferredSlots.some(s => s.convertedTime === slot.convertedTime)
                        ? "hover:bg-gradient-to-r hover:from-purple-100 hover:via-pink-100 hover:to-orange-100 hover:border-purple-300 hover:text-purple-700"
                        : "hover:border-primary hover:text-primary"
                  )}
                  data-testid={`slot-${slot.displayTime.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
                  onClick={() => handleTimeClick(slot.convertedTime)}
                >
                  {slot.displayTime}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Debug: fetch calendar connections for the organizer
  const [calendarConnections, setCalendarConnections] = useState<any[] | null>(null);
  React.useEffect(() => {
    if (troubleshootMode && organizerUserId) {
      getAllCalendarConnectionsForUser(organizerUserId).then(setCalendarConnections);
    }
  }, [troubleshootMode, organizerUserId]);

  return (
    <div className={cn("flex flex-col flex-1 min-h-0", className)} data-testid="time-slots">
      {/* Date and Controls Header - Fixed */}
      <div className="flex-shrink-0 space-y-4 mb-2">
        <div className="flex items-center justify-between md:justify-normal lg:justify-between">
          {/* Date and Mobile Navigation - Left side */}
          <div className="flex items-center gap-2">
            <h3 className="md:text-sm lg:text-md font-medium text-gray-900">
              {getDateLabel(selectedDate)}
            </h3>
            
            {/* Mobile Navigation - Compact buttons next to date */}
            <div className="flex items-center gap-1 sm:hidden">
              <Button variant="outline" size="sm" onClick={onPrevDay} className="h-8 w-8 p-0">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Button variant="outline" size="sm" onClick={onNextDay} className="h-8 w-8 p-0">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Timezone and Time Format - Right aligned */}
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-0.5">
              <Globe className="h-3 w-3 text-gray-500" />
              <span className="text-xs font-medium text-gray-700">
                {timezoneInfo.abbreviation}
              </span>
            </div>
            
            <div className="flex bg-gray-100 rounded-md p-1 md:p-0 lg:p-1">
              <button
                onClick={toggleTimeFormat}
                className={cn(
                  "px-1 py-1 text-xs font-medium rounded transition-colors",
                  timeFormat === '12h'
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                12hr
              </button>
              <button
                onClick={toggleTimeFormat}
                className={cn(
                  "px-2 py-1 text-xs font-medium rounded transition-colors",
                  timeFormat === '24h'
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                24hr
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Time Slots Content - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {false ? (
          <LoadingIndicator variant="skeleton" />
        ) : (
          <div className="space-y-6">
          {/* Celebration indicator for optimal slot selection - only if not "any" preference */}
          {isOptimalSlot && selectedTime && energyPreference !== 'any' && (
            <div className={cn(
              "flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-purple-100 via-pink-100 to-orange-100 border border-purple-200 rounded-lg",
              notificationFeatures["notifications-fx"] && "animate-pulse"
            )}>
              <Sparkles className={cn(
                "h-4 w-4 text-purple-600",
                notificationFeatures["notifications-fx"] && "animate-bounce"
              )} />
              <span className="text-sm font-medium text-purple-800">
                Great choice! This time is optimized for your energy levels ✨
              </span>
            </div>
          )}

          {/* Feature-flagged notification bell for slot selection */}
          {notificationFeatures["notifications-bell"] && selectedTime && (
            <div className="flex items-center justify-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <Bell className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-700">
                You'll receive booking confirmations via email
              </span>
            </div>
          )}
          {shouldShowLoadingMessage && (
            <div className="flex items-center justify-center py-8">
              <LoadingIndicator variant="inline" message="Finding available times..." />
            </div>
          )}

          {shouldShowCalendarError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Unable to verify calendar availability. The host's calendar connection may have expired. 
                Please try again later or contact the host to reconnect their calendar.
              </AlertDescription>
            </Alert>
          )}
          
          {shouldShowEmptyState && (
            <Card className="p-6 text-center">
              <CardContent className="p-0">
                <Clock className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                <h3 className="font-medium text-gray-900 mb-1">
                  No times available
                </h3>
                <p className="text-sm text-gray-500">
                  Please try selecting a different date
                </p>
              </CardContent>
            </Card>
          )}
          
          {shouldShowSlots && (
  <div className="space-y-6">
    {/* Enhanced mode: Show preferred slots first with special styling - ONLY if not "any" preference */}
    {preferredSlots.length > 0 && energyPreference !== 'any' && (
      <div className="bg-gradient-to-br from-blue-100/90 via-purple-100/50 to-purple-200/70 border-0 rounded-2xl p-4">
        {renderCategorizedSlots(
          preferredSlots, 
          "Optimal Times", 
          <Dna className="h-4 w-4 text-zinc-500" />
        )}
        <p className="text-xs text-medium text-blue-900/90 mt-3">
          Suggested based on the host's biological rhythms. <a href={`http://chronocal.io?utm_source=booking_page&utm_medium=footer&utm_campaign=meeting_links&utm_content=${meetingLinkId}`} target="_blank" className="underline text-zinc-900/70 hover:text-zinc-900/100">Learn more</a>
        </p>
      </div>
    )}
    
    {/* If "any" preference, show all preferred slots as regular available times */}
    {preferredSlots.length > 0 && energyPreference === 'any' && (
      <div className="space-y-4">

        {renderCategorizedSlots(preferredSlots)}
      </div>
    )}
    
    {/* Enhanced mode: Show regular available slots (NON-OPTIMAL only) - only if not "any" preference */}
    {regularSlots.length > 0 && energyPreference !== 'any' && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">Available times</h4>
          <span className="text-sm text-gray-500">
            {regularSlots.length} slot{regularSlots.length !== 1 ? 's' : ''} available
          </span>
        </div>
        {renderCategorizedSlots(regularSlots)}
      </div>
    )}
    
    {/* Regular mode: Show all slots when NOT in enhanced mode */}
    {allSlots.length > 0 && preferredSlots.length === 0 && regularSlots.length === 0 && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">Available times</h4>
          <span className="text-sm text-gray-500">
            {allSlots.length} slot{allSlots.length !== 1 ? 's' : ''} available
          </span>
        </div>
        {renderCategorizedSlots(allSlots)}
      </div>
    )}
  </div>
)}
          </div>
        )}
      </div>

      {troubleshootMode && (
        <div className="mt-4 p-3 bg-gray-50 rounded text-xs">
          <p><strong>Debug Info:</strong></p>
          <p>Selected Date: {selectedDate.toDateString()}</p>
          <p>Show All Slots: {showAllSlots.toString()}</p>
          <p>Enhanced Mode: {(!!enhancedSlotsForDate).toString()}</p>
          <p>Enhanced Slots Data: {enhancedSlotsForDate ? JSON.stringify(enhancedSlotsForDate) : 'null'}</p>
          <p>Preferred Slots: {preferredSlots.length}</p>
          <p>Regular Slots: {regularSlots.length}</p>
          <p>All Slots: {allSlots.length}</p>
          <p>Available Slots Array: {availableSlots.length} - {JSON.stringify(availableSlots.slice(0, 3))}...</p>
          <p>Organizer Timezone: {organizerTimezone}</p>
          <p>Selected Timezone: {selectedTimezone}</p>
          <p>Timezone Abbreviation: {timezoneInfo.abbreviation}</p>
          <p>Loading: {loading.toString()}</p>
          <p>Should Show Skeleton: {shouldShowSkeleton.toString()}</p>
          <p>Has Calendar Error: {hasCalendarError.toString()}</p>
          <p>Connected Calendars: {calendarConnections ? calendarConnections.length : '...'}</p>
          <p>Calendar Emails: {calendarConnections ? calendarConnections.map((c: any) => c.email).join(', ') : '...'}</p>
          <p><strong>Feature Flags:</strong></p>
          <p>Notifications: {notificationsEnabled.toString()}</p>
          <p>Notifications Bell: {notificationFeatures["notifications-bell"]?.toString() ?? 'undefined'}</p>
          <p>Notifications Popup: {notificationFeatures["notifications-popup"]?.toString() ?? 'undefined'}</p>
          <p>Notifications FX: {notificationFeatures["notifications-fx"]?.toString() ?? 'undefined'}</p>
        </div>
      )}
    </div>
  );
};
